package com.evoapps.evoshift.ui.user

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.TopBarAction
import com.evoapps.evoshift.widgets.helpAction
import com.evoapps.evoshift.ui.notifications.notificationsAction
import com.evoapps.evoshift.ui.util.HelpCentreButton
import com.evoapps.evoshift.ui.util.HelpPage
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.NotificationCategory
import com.evoapps.evoshift.ui.adaptive.ContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.NotificationSettingsViewModel
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.back
import evoshift.composeapp.generated.resources.notif_category_absences
import evoshift.composeapp.generated.resources.notif_category_announcements
import evoshift.composeapp.generated.resources.notif_category_announcements_desc
import evoshift.composeapp.generated.resources.notif_category_absences_desc
import evoshift.composeapp.generated.resources.notif_category_availability
import evoshift.composeapp.generated.resources.notif_category_availability_desc
import evoshift.composeapp.generated.resources.notif_category_no_shows
import evoshift.composeapp.generated.resources.notif_category_no_shows_desc
import evoshift.composeapp.generated.resources.notif_category_checklists
import evoshift.composeapp.generated.resources.notif_category_hiring_desc
import evoshift.composeapp.generated.resources.notif_category_hiring
import evoshift.composeapp.generated.resources.notif_category_checklists_desc
import evoshift.composeapp.generated.resources.notif_category_roster
import evoshift.composeapp.generated.resources.notif_category_roster_desc
import evoshift.composeapp.generated.resources.notif_category_swaps
import evoshift.composeapp.generated.resources.notif_category_swaps_desc
import evoshift.composeapp.generated.resources.notif_settings_intro
import evoshift.composeapp.generated.resources.notif_settings_title
import evoshift.composeapp.generated.resources.notif_trial_emails
import evoshift.composeapp.generated.resources.notif_trial_emails_desc
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/**
 * Which kinds of notification reach the person's devices as a push (SH-73), from the profile.
 * The in-app list keeps all of them; managers also see absence requests and no-shows.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun NotificationSettingsScreen(
    viewModel: NotificationSettingsViewModel,
    onNavigateBack: () -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val errorText = state.error?.asString()
    LaunchedEffect(errorText) {
        errorText?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            EvoTopBar(
                title = stringResource(Res.string.notif_settings_title),
                onBack = onNavigateBack,
                actions = listOf(helpAction(HelpPage.NOTIFICATION_SETTINGS))
            )
        }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize()) {
            if (state.isLoading) {
                CircularProgressIndicator(Modifier.align(Alignment.Center))
                return@Box
            }
            Column(
                modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).contentWidth(ContentMaxWidth).padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Text(
                    stringResource(Res.string.notif_settings_intro),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
                state.categories.forEach { category ->
                    PushSwitchRow(
                        title = stringResource(category.title),
                        description = stringResource(category.description),
                        checked = state.settings.isOn(category),
                        onCheckedChange = { viewModel.setPush(category, it) }
                    )
                }
                // SH-241: owners' tips by e-mail during the trial (their unsubscribe link sets this too).
                if (state.showsTrialEmails) {
                    PushSwitchRow(
                        title = stringResource(Res.string.notif_trial_emails),
                        description = stringResource(Res.string.notif_trial_emails_desc),
                        checked = state.settings.trialEmails,
                        onCheckedChange = { viewModel.setTrialEmails(it) }
                    )
                }
            }
        }
    }
}

val NotificationCategory.title: StringResource
    get() = when (this) {
        NotificationCategory.ROSTER -> Res.string.notif_category_roster
        NotificationCategory.SWAPS -> Res.string.notif_category_swaps
        NotificationCategory.ABSENCES -> Res.string.notif_category_absences
        NotificationCategory.AVAILABILITY -> Res.string.notif_category_availability
        NotificationCategory.NO_SHOWS -> Res.string.notif_category_no_shows
        NotificationCategory.ANNOUNCEMENTS -> Res.string.notif_category_announcements
        NotificationCategory.CHECKLISTS -> Res.string.notif_category_checklists
        NotificationCategory.HIRING -> Res.string.notif_category_hiring
    }

val NotificationCategory.description: StringResource
    get() = when (this) {
        NotificationCategory.ROSTER -> Res.string.notif_category_roster_desc
        NotificationCategory.SWAPS -> Res.string.notif_category_swaps_desc
        NotificationCategory.ABSENCES -> Res.string.notif_category_absences_desc
        NotificationCategory.AVAILABILITY -> Res.string.notif_category_availability_desc
        NotificationCategory.NO_SHOWS -> Res.string.notif_category_no_shows_desc
        NotificationCategory.ANNOUNCEMENTS -> Res.string.notif_category_announcements_desc
        NotificationCategory.CHECKLISTS -> Res.string.notif_category_checklists_desc
        NotificationCategory.HIRING -> Res.string.notif_category_hiring_desc
    }

/** The whole row toggles (one tap target, read as one switch by screen readers). */
@Composable
private fun PushSwitchRow(title: String, description: String, checked: Boolean, onCheckedChange: (Boolean) -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))
            .toggleable(value = checked, role = Role.Switch, onValueChange = onCheckedChange)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.weight(1f)) {
            Text(title, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Medium)
            Text(description, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        Spacer(Modifier.width(12.dp))
        Switch(checked = checked, onCheckedChange = null)
    }
}
